import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Star, AlertTriangle, PhoneCall, CheckCircle2, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { BACKEND, api, errMsg, getToken } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Btn, Chip, Empty, PageHeader, Spinner } from "@/components/rp";
import { ReviewUrlSetting } from "@/components/admin/fineLavori";
import { RecontactDialog, TrendChart } from "@/components/admin/satisfaction";

const PERIODS = [["tutto", "Tutte"], ["30", "Ultimi 30 giorni"], ["90", "Ultimi 90 giorni"], ["365", "Ultimo anno"]];
const fmtAvg = (v) => (v == null ? "—" : v.toFixed(1).replace(".", ","));

const Stars = ({ value, size = "h-4 w-4" }) => (
  <span className="inline-flex gap-0.5" aria-label={`${value} su 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`${size} ${i <= Math.round(value || 0) ? "fill-[#070707] text-[#070707]" : "text-[#C8BDAB]"}`} strokeWidth={1.5} />
    ))}
  </span>
);

const StatCard = ({ label, value, testid, big }) => (
  <div className={`rp-card p-5 sm:p-6 ${big ? "bg-[#070707] text-white border-[#070707]" : ""}`} data-testid={testid}>
    <p className={`rp-label ${big ? "!text-white/70" : ""}`}>{label}</p>
    <p className="text-4xl font-semibold mt-3 tracking-tight">{fmtAvg(value)}<span className="text-base font-normal opacity-60"> /5</span></p>
    {!big && <div className="mt-2"><Stars value={value} /></div>}
  </div>
);

const Distribution = ({ dist, count }) => (
  <div className="rp-card p-5 sm:p-6" data-testid="satisfaction-distribution">
    <p className="rp-label mb-4">Distribuzione media per verbale</p>
    {[5, 4, 3, 2, 1].map((s) => {
      const n = dist?.[s] || 0;
      return (
        <div key={s} className="flex items-center gap-3 text-sm mb-2">
          <span className="w-10">{s} ★</span>
          <div className="flex-1 h-2 bg-[#E6DDCE] rounded-full overflow-hidden"><div className="h-full bg-[#070707] transition-[width] duration-500" style={{ width: `${count ? (n / count) * 100 : 0}%` }} /></div>
          <span className="w-8 text-right text-[#757575]" data-testid={`satisfaction-dist-${s}`}>{n}</span>
        </div>
      );
    })}
  </div>
);

const Recontact = ({ it, onOpen }) => (
  <div className="w-full border-t border-[#EBEBEB] pt-3 flex flex-wrap items-center gap-3">
    {it.recontact ? (
      <p className="text-sm text-[#198754] flex gap-2" data-testid={`recontact-info-${it.order_id}`}>
        <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
        <span>Ricontattato il {fmtDate(it.recontact.at)} da {it.recontact.by_name} — «{it.recontact.note}»</span>
      </p>
    ) : (
      <>
        <span className="text-sm text-[#B3261E]" data-testid={`recontact-todo-${it.order_id}`}>Voto basso: da ricontattare</span>
        <Btn variant="outline" className="h-9 ml-auto" onClick={() => onOpen(it)} data-testid={`recontact-button-${it.order_id}`}><PhoneCall className="h-4 w-4" /> Cliente ricontattato</Btn>
      </>
    )}
  </div>
);

const Row = ({ it, onOpen }) => (
  <div className="rp-card p-4 sm:px-6 flex flex-wrap items-center gap-4 hover:border-[#C8BDAB] transition-colors" data-testid={`satisfaction-row-${it.order_id}`}>
    <div className="flex-1 min-w-[200px]">
      <Link to={`/admin/commesse/${it.order_id}`} className="font-medium hover:underline" data-testid={`satisfaction-link-${it.order_id}`}>{it.code} · {it.title}</Link>
      <p className="text-sm text-[#757575]">{it.client_name} · firmato il {fmtDate(it.at)}{it.by_name && ` da ${it.by_name}`}</p>
      {it.note && <p className="text-sm mt-1 italic">«{it.note}»</p>}
      {it.has_problem && <p className="text-sm mt-1 text-[#B3261E] flex items-center gap-1"><AlertTriangle className="h-4 w-4" />Problema segnalato</p>}
    </div>
    <div className="grid grid-cols-3 gap-4 text-xs text-[#757575]">
      {[["Progetto", it.rating_product], ["Staff", it.rating_seller], ["Imprese", it.rating_team]].map(([l, v]) => (
        <div key={l}><p className="mb-1">{l}</p><Stars value={v} size="h-3.5 w-3.5" /></div>
      ))}
    </div>
    {it.low && <Recontact it={it} onOpen={onOpen} />}
  </div>
);

export default function AdminSoddisfazione() {
  const [period, setPeriod] = useState("tutto");
  const [data, setData] = useState(null);
  const [todo, setTodo] = useState(false);
  const [dialog, setDialog] = useState(null);
  const onSaved = (id, rc) => {
    setData((d) => ({ ...d, items: d.items.map((x) => (x.order_id === id ? { ...x, recontact: rc } : x)) }));
    setDialog(null);
  };

  useEffect(() => {
    setData(null);
    api.get("/admin/soddisfazione", { params: { period } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period]);

  const s = data?.stats;
  const items = (data?.items || []).filter((x) => !todo || (x.low && !x.recontact));
  return (
    <div data-testid="admin-satisfaction-page">
      <PageHeader label="Sondaggio soddisfazione" title="Valutazioni dei clienti">
        <a href={`${BACKEND}/api/admin/soddisfazione/export.xlsx?${new URLSearchParams({ period, todo, token: getToken() })}`} data-testid="satisfaction-export-button"
          className="rp-btn bg-[#070707] text-white hover:bg-black/80"><FileSpreadsheet className="h-4 w-4" /> Esporta Excel ({items.length})</a>
      </PageHeader>
      <ReviewUrlSetting />
      <div className="flex flex-wrap gap-2 mb-6" data-testid="satisfaction-period-filter">
        {PERIODS.map(([v, l]) => <Chip key={v} active={period === v} onClick={() => setPeriod(v)} data-testid={`satisfaction-period-${v}`}>{l}</Chip>)}
        <Chip active={todo} onClick={() => setTodo(!todo)} data-testid="satisfaction-filter-todo">Da ricontattare ({(data?.items || []).filter((x) => x.low && !x.recontact).length})</Chip>
      </div>
      {dialog && <RecontactDialog item={dialog} onClose={() => setDialog(null)} onSaved={onSaved} />}
      {!data ? <Spinner /> : (
        <>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 rp-fade">
            <StatCard big label={`Media generale · ${s.count} verbali`} value={s.avg_overall} testid="satisfaction-avg-overall" />
            <StatCard label="Progetto" value={s.avg_product} testid="satisfaction-avg-product" />
            <StatCard label="Staff" value={s.avg_seller} testid="satisfaction-avg-seller" />
            <StatCard label="Imprese" value={s.avg_team} testid="satisfaction-avg-team" />
          </div>
          <TrendChart trend={data.trend} />
          <div className="grid lg:grid-cols-[1fr_2fr] gap-4 items-start">
            <Distribution dist={s.distribution} count={s.count} />
            <div className="space-y-3" data-testid="satisfaction-list">
              {items.length ? items.map((it) => <Row key={it.order_id} it={it} onOpen={setDialog} />)
                : <Empty icon={Star} title="Nessuna valutazione" text="Le valutazioni compaiono quando il cliente firma il verbale di fine lavori." data-testid="satisfaction-empty" />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
